/**
 * StudioCommand — global cross-channel command center.
 *
 * Replaces StudioHub as the main /studio entry. Covers:
 *  · Cross-channel KPI strip (total subs, views, pending, delayed, published 7d)
 *  · Channel overview cards (subs, views, pending, next deadline, growth %, team)
 *  · Pending Tasks table (everything in flight, sorted by due date)
 *  · Tabular Content Pipeline view (filter by channel/stage, search, sort)
 *  · Embedded video preview modal
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Youtube, Loader2, AlertTriangle, ChevronRight, Users, Calendar, TrendingUp,
  Eye, AlertCircle, CheckCircle2, X, Play, Search, ArrowUpDown, RefreshCw,
  Plus, BarChart3, Radio, Activity, Sparkles, FileEdit, Send,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useStudioFeed, { eventLabel } from "./useStudioFeed";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STAGE_LABEL = {
  idea: "Idea", approved: "Approved", scripting: "Scripting", script_review: "Script Review",
  recording: "Recording", raw_uploaded: "Raw uploaded", editing: "Editing", edit_review: "Edit Review",
  thumbnail: "Thumbnail", thumbnail_review: "Thumb Review", seo: "SEO",
  scheduling: "Scheduling", scheduled: "Scheduled", published: "Published", archived: "Archived",
};

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { month: "short", day: "numeric" }); }
  catch (e) { return "—"; }
}
function daysFromNow(iso) {
  if (!iso) return null;
  const diff = (new Date(iso) - new Date()) / (1000 * 60 * 60 * 24);
  return Math.round(diff);
}

export default function StudioCommand() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoOpen, setVideoOpen] = useState(null);  // {url, title, meta}
  const { connected, events, subscribe } = useStudioFeed();

  const refresh = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/api/studio/command/overview`, { withCredentials: true });
      setOverview(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Subscribe to every channel the user is part of as soon as overview loads.
  useEffect(() => {
    if (overview?.channels?.length) {
      subscribe(overview.channels.map((c) => c.channel_id));
    }
  }, [overview?.channels, subscribe]);

  // Auto-refresh overview when a meaningful event fires.
  useEffect(() => {
    if (!events.length) return;
    const last = events[0];
    if (["content_field_edited", "seo_applied", "asset_uploaded"].includes(last?.type)) {
      refresh();
    }
  }, [events]);

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <span className="studio-eyebrow">
            <BarChart3 size={12}/> Command center · global view
          </span>
          <h1 className="studio-h1">
            All your YouTube channels, <span className="studio-h1-accent">at one glance.</span>
          </h1>
          <p className="studio-sub">
            Cross-channel KPIs, pending tasks, and the full content pipeline — everything in flight, sorted.
          </p>
          <div className="studio-hero-cta">
            <button className="studio-btn-ghost" onClick={refresh} data-testid="cmd-refresh">
              {loading ? <Loader2 size={14} className="ac-spin"/> : <RefreshCw size={14}/>} Refresh
            </button>
            <Link to="/studio/topic-suggestions" className="studio-btn-primary" data-testid="cmd-topics">
              <Plus size={14}/> AI Topics
            </Link>
            <Link to="/studio/reports" className="studio-btn-ghost" data-testid="cmd-reports">
              Reports →
            </Link>
            <LiveFeedIndicator connected={connected} count={events.length} />
          </div>
        </div>

        {error && <div className="ac-error"><AlertTriangle size={16}/> {error}</div>}

        {!loading && overview && events.length > 0 && (
          <LiveFeedStream events={events.slice(0, 10)} />
        )}

        {loading && (
          <div data-testid="cmd-skeletons">
            <div className="studio-ops-row">
              {[1,2,3,4,5].map((i) => <div key={i} className="studio-skeleton studio-sk-card"/>)}
            </div>
            <div className="studio-grid" style={{ marginTop: 18 }}>
              {[1,2,3].map((i) => <div key={i} className="studio-skeleton studio-sk-card" style={{ height: 180 }}/>)}
            </div>
          </div>
        )}

        {!loading && overview && (
          <>
            <KPIStrip kpis={overview.kpis} />
            <ChannelCards channels={overview.channels} />
            <PendingTable rows={overview.pending_top || []} />
            <TabularPipeline channels={overview.channels} onOpenVideo={setVideoOpen} />
          </>
        )}
      </div>

      {videoOpen && <VideoModal payload={videoOpen} onClose={() => setVideoOpen(null)} />}
      <Footer />
    </>
  );
}

function KPIStrip({ kpis }) {
  const items = [
    { label: "Channels", val: kpis.total_channels, icon: Youtube },
    { label: "Subscribers", val: fmtNum(kpis.total_subscribers), icon: Users },
    { label: "Total views", val: fmtNum(kpis.total_views), icon: Eye },
    { label: "Pending", val: kpis.total_pending, icon: AlertCircle },
    { label: "Delayed", val: kpis.delayed, icon: AlertTriangle, danger: kpis.delayed > 0 },
    { label: "Published · 7d", val: kpis.published_7d, icon: CheckCircle2 },
  ];
  return (
    <div className="studio-ops-row" data-testid="cmd-kpi-strip">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.label} className="studio-ops-card" data-testid={`cmd-kpi-${it.label.toLowerCase().replace(/\W+/g, "-")}`}>
            <header>
              <span className="studio-eyebrow"><Icon size={12}/> {it.label}</span>
            </header>
            <p style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 0", color: it.danger ? "#ef4444" : "var(--st-text)" }}>
              {it.val ?? 0}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ChannelCards({ channels }) {
  if (!channels?.length) {
    return (
      <div className="studio-empty" data-testid="cmd-no-channels">
        <div className="studio-empty-icon"><Youtube size={36}/></div>
        <h2>No channels connected yet</h2>
        <p>Connect a YouTube channel from the dashboard to see KPIs here.</p>
      </div>
    );
  }
  return (
    <>
      <div className="studio-section-h"><h2>Channel overview</h2></div>
      <div className="studio-grid" data-testid="cmd-channels-grid">
        {channels.map((c) => {
          const nd = daysFromNow(c.next_deadline);
          return (
            <motion.div
              key={c.channel_id}
              className="studio-channel-card"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`cmd-channel-${c.channel_id}`}
            >
              <div className="studio-channel-head">
                <div className="studio-channel-meta" style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "1rem" }}>{c.title}</h3>
                  <p>{c.handle || c.niche || "—"}</p>
                </div>
                {c.thumbnail_url && (
                  <img src={c.thumbnail_url} alt="" style={{ width: 44, height: 44, borderRadius: 10 }} />
                )}
              </div>
              <div className="studio-channel-stats">
                <div className="studio-stat">
                  <span className="studio-stat-icon"><Users size={14}/></span>
                  <div><strong>{fmtNum(c.subscribers)}</strong><small>Subs</small></div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><Eye size={14}/></span>
                  <div><strong>{fmtNum(c.views)}</strong><small>Views</small></div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><TrendingUp size={14}/></span>
                  <div><strong>{(c.growth_pct || 0) > 0 ? "+" : ""}{c.growth_pct || 0}%</strong><small>Growth 7d</small></div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><AlertCircle size={14}/></span>
                  <div><strong>{c.pending_tasks}</strong><small>Pending</small></div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><Calendar size={14}/></span>
                  <div>
                    <strong>{c.next_deadline ? (nd === 0 ? "Today" : nd < 0 ? `Overdue ${Math.abs(nd)}d` : `${nd}d`) : "—"}</strong>
                    <small>Next deadline</small>
                  </div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><Users size={14}/></span>
                  <div><strong>{c.team_members}</strong><small>Team</small></div>
                </div>
              </div>
              <div className="studio-channel-foot">
                <span className="studio-pill">Last: {fmtDate(c.last_upload)}</span>
                <Link to={`/studio/channels/${c.channel_id}`} className="studio-go" data-testid={`cmd-open-${c.channel_id}`}>
                  Open <ChevronRight size={14}/>
                </Link>
              </div>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

function PendingTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <>
      <div className="studio-section-h" style={{ marginTop: 26 }}>
        <h2><AlertCircle size={16}/> Pending tasks · top 10</h2>
      </div>
      <div className="studio-pipe-table-wrap" data-testid="cmd-pending-table">
        <table className="studio-pipe-table">
          <thead>
            <tr>
              <th>Channel</th><th>Task</th><th>Stage</th><th>Due</th><th>Priority</th><th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const nd = daysFromNow(r.due_date);
              return (
                <tr key={r._id} data-testid={`cmd-pending-row-${r._id}`}>
                  <td><strong>{r.channel_title || "—"}</strong></td>
                  <td>{r.title}</td>
                  <td><span className="stage-pill">{STAGE_LABEL[r.stage] || r.stage}</span></td>
                  <td style={{ color: nd != null && nd < 0 ? "#ef4444" : undefined }}>
                    {r.due_date ? `${fmtDate(r.due_date)}${nd != null ? ` · ${nd < 0 ? `${Math.abs(nd)}d late` : `${nd}d`}` : ""}` : "—"}
                  </td>
                  <td className={`prio-${r.priority || "low"}`}>{(r.priority || "—").toUpperCase()}</td>
                  <td>
                    <span className="studio-progress"><div style={{ width: `${r.progress_pct || 0}%` }}/></span>
                    <small style={{ marginLeft: 6, color: "var(--st-text-muted)" }}>{r.progress_pct || 0}%</small>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TabularPipeline({ channels, onOpenVideo }) {
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chFilter, setChFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("due_date");
  const [sortDir, setSortDir] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStage, setBulkStage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (chFilter) params.set("channel_id", chFilter);
      if (stageFilter) params.set("stage", stageFilter);
      if (q) params.set("q", q);
      const r = await axios.get(`${API}/api/studio/command/pipeline?${params}`, { withCredentials: true });
      setPipeline(r.data);
      setSelected(new Set());
      setPage(1);
    } catch (err) {
      setPipeline({ rows: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [chFilter, stageFilter]);

  const sorted = useMemo(() => {
    if (!pipeline?.rows) return [];
    return [...pipeline.rows].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      return av > bv ? sortDir : av < bv ? -sortDir : 0;
    });
  }, [pipeline, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const pageRows = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(-sortDir);
    else { setSortKey(k); setSortDir(1); }
  };

  const togglePick = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const pickAllOnPage = () => {
    const allPicked = pageRows.every((r) => selected.has(r._id));
    const next = new Set(selected);
    if (allPicked) pageRows.forEach((r) => next.delete(r._id));
    else pageRows.forEach((r) => next.add(r._id));
    setSelected(next);
  };

  const bulkApply = async (action, payload = {}) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/studio/crm/content/bulk`,
        { content_ids: Array.from(selected), action, ...payload },
        { withCredentials: true },
      );
      const applied = r.data?.applied || 0;
      const skipped = (r.data?.skipped || []).length;
      window.alert(`Applied ${applied}${skipped ? ` · ${skipped} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Bulk action failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <>
      <div className="studio-section-h" style={{ marginTop: 26 }}>
        <h2>Content pipeline · tabular</h2>
      </div>
      <div className="studio-card" style={{ padding: 12, marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
        <select className="studio-input" value={chFilter} onChange={(e) => setChFilter(e.target.value)} data-testid="cmd-pipe-channel-filter">
          <option value="">All channels</option>
          {channels.map((c) => <option key={c.channel_id} value={c.channel_id}>{c.title}</option>)}
        </select>
        <select className="studio-input" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="cmd-pipe-stage-filter">
          <option value="">All stages</option>
          {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input
          className="studio-input"
          placeholder="Search title…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="cmd-pipe-search"
          style={{ flex: 1, minWidth: 200 }}
        />
        <button className="studio-btn-ghost" onClick={load} data-testid="cmd-pipe-apply">
          <Search size={14}/> Apply
        </button>
      </div>

      {/* Floating bulk action bar */}
      {selected.size > 0 && (
        <div className="studio-bulk-bar" data-testid="cmd-bulk-bar">
          <strong data-testid="cmd-bulk-count">{selected.size} selected</strong>
          <select
            className="studio-input"
            value={bulkStage}
            onChange={(e) => setBulkStage(e.target.value)}
            data-testid="cmd-bulk-stage-select"
            style={{ minWidth: 160 }}
          >
            <option value="">Move to stage…</option>
            {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button
            className="studio-btn-primary"
            disabled={bulkBusy || !bulkStage}
            onClick={() => bulkApply("set_stage", { stage: bulkStage })}
            data-testid="cmd-bulk-set-stage"
            style={{ padding: "8px 12px" }}
          >Apply stage</button>
          <button
            className="studio-btn-ghost"
            disabled={bulkBusy}
            onClick={() => bulkApply("archive")}
            data-testid="cmd-bulk-archive"
            style={{ padding: "8px 12px" }}
          >Archive</button>
          <button
            className="studio-btn-ghost"
            disabled={bulkBusy}
            onClick={() => { if (window.confirm(`Delete ${selected.size} item(s)? This moves them to trash.`)) bulkApply("delete"); }}
            data-testid="cmd-bulk-delete"
            style={{ padding: "8px 12px", color: "#EF4444", borderColor: "#FCA5A5" }}
          >Delete</button>
          <button
            className="studio-btn-ghost"
            onClick={() => setSelected(new Set())}
            data-testid="cmd-bulk-clear"
            style={{ padding: "8px 12px", marginLeft: "auto" }}
          ><X size={12}/> Clear</button>
        </div>
      )}

      <div className="studio-pipe-table-wrap" data-testid="cmd-pipeline-table">
        <table className="studio-pipe-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={pageRows.length > 0 && pageRows.every((r) => selected.has(r._id))}
                  onChange={pickAllOnPage}
                  data-testid="cmd-pipe-pick-all"
                  aria-label="Select all rows on this page"
                />
              </th>
              {[
                ["channel_title", "Channel"], ["title", "Video"], ["stage", "Stage"],
                ["due_date", "Due"], ["priority", "Priority"], ["progress_pct", "Progress"],
                ["actions", "Actions"],
              ].map(([k, label]) => (
                <th key={k} onClick={() => k !== "actions" && toggleSort(k)} style={{ cursor: k !== "actions" ? "pointer" : "default" }}>
                  {label} {sortKey === k && k !== "actions" && <ArrowUpDown size={11} style={{ verticalAlign: "-2px" }}/>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8}><div className="studio-skeleton studio-sk-row" style={{ height: 30 }}/></td></tr>
            )}
            {!loading && pageRows.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 20, color: "var(--st-text-muted)" }}>
                No items matching the filters.
              </td></tr>
            )}
            {!loading && pageRows.map((r) => {
              const nd = daysFromNow(r.due_date);
              const picked = selected.has(r._id);
              return (
                <tr key={r._id} data-testid={`cmd-pipe-row-${r._id}`} style={picked ? { background: "rgba(124,58,237,0.06)" } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={picked}
                      onChange={() => togglePick(r._id)}
                      data-testid={`cmd-pipe-pick-${r._id}`}
                      aria-label={`Select ${r.title}`}
                    />
                  </td>
                  <td><strong>{r.channel_title || "—"}</strong></td>
                  <td>{r.title}</td>
                  <td><span className="stage-pill">{STAGE_LABEL[r.stage] || r.stage}</span></td>
                  <td style={{ color: nd != null && nd < 0 ? "#ef4444" : undefined }}>
                    {r.due_date ? fmtDate(r.due_date) : "—"}
                  </td>
                  <td className={`prio-${r.priority || "low"}`}>{(r.priority || "—").toUpperCase()}</td>
                  <td>
                    <span className="studio-progress"><div style={{ width: `${r.progress_pct || 0}%` }}/></span>
                  </td>
                  <td>
                    {r.youtube_video_id && (
                      <button
                        className="studio-btn-ghost"
                        onClick={() => onOpenVideo({
                          url: `https://www.youtube.com/embed/${r.youtube_video_id}`,
                          title: r.title,
                          meta: r,
                        })}
                        data-testid={`cmd-pipe-play-${r._id}`}
                        style={{ padding: "4px 8px" }}
                      >
                        <Play size={12}/> Preview
                      </button>
                    )}
                    <Link
                      to={`/studio/channels/${r.channel_id}?content=${r._id}`}
                      className="studio-btn-ghost"
                      data-testid={`cmd-pipe-open-${r._id}`}
                      style={{ padding: "4px 8px", marginLeft: 4 }}
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {sorted.length > perPage && (
        <div className="studio-pagination" data-testid="cmd-pipe-pagination">
          <button
            className="studio-btn-ghost"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="cmd-pipe-prev-page"
          >‹ Prev</button>
          <span style={{ color: "var(--st-text-soft)", fontSize: 13 }}>
            Page <strong data-testid="cmd-pipe-current-page">{page}</strong> / {totalPages}
            <span style={{ marginLeft: 12, color: "var(--st-text-muted)" }}>
              · {sorted.length} item(s)
            </span>
          </span>
          <button
            className="studio-btn-ghost"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="cmd-pipe-next-page"
          >Next ›</button>
        </div>
      )}
    </>
  );
}

function VideoModal({ payload, onClose }) {
  return (
    <div className="studio-vid-overlay" onClick={onClose} data-testid="cmd-video-modal">
      <div className="studio-vid-modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>{payload.title}</h3>
          <button className="studio-btn-ghost" onClick={onClose} data-testid="cmd-video-close">
            <X size={14}/>
          </button>
        </header>
        <div className="studio-vid-embed">
          <iframe src={payload.url} title={payload.title} allow="autoplay; encrypted-media" allowFullScreen />
        </div>
        <div className="studio-vid-meta">
          Stage: <strong>{STAGE_LABEL[payload.meta.stage] || payload.meta.stage}</strong> ·
          {" "}Due: <strong>{fmtDate(payload.meta.due_date)}</strong>
        </div>
      </div>
    </div>
  );
}

const EVENT_ICONS = {
  content_field_edited: FileEdit,
  seo_job_complete: Sparkles,
  seo_applied: Send,
  asset_uploaded: Activity,
  asset_replaced: Activity,
  content_created: Plus,
  approval_decided: CheckCircle2,
};

function LiveFeedIndicator({ connected, count }) {
  return (
    <span
      className="studio-pill"
      data-testid="cmd-live-indicator"
      title={connected ? "Realtime feed connected" : "Realtime feed disconnected — auto-reconnecting"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
        background: connected ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
        border: `1px solid ${connected ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}`,
        color: connected ? "#10b981" : "#ef4444",
        fontSize: 12, fontWeight: 600,
      }}
    >
      <Radio size={12} style={{ animation: connected ? "ac-pulse 1.6s ease-in-out infinite" : "none" }}/>
      {connected ? "LIVE" : "Reconnecting…"}
      {count > 0 && <small style={{ opacity: 0.7 }}>· {count}</small>}
    </span>
  );
}

function LiveFeedStream({ events }) {
  if (!events.length) return null;
  return (
    <div className="studio-card" style={{ padding: 14, marginBottom: 14 }} data-testid="cmd-live-feed">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Activity size={14} style={{ color: "#a78bfa" }} />
        <strong style={{ fontSize: 13 }}>Live activity</strong>
        <small className="studio-muted" style={{ marginLeft: "auto" }}>updates instantly · last {events.length}</small>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
        {events.map((ev, i) => {
          const Icon = EVENT_ICONS[ev.type] || Activity;
          const when = ev.ts ? new Date(ev.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
          const who = ev.data?.actor_name || (ev.actor_id ? `User ${String(ev.actor_id).slice(-4)}` : "Someone");
          const what = eventLabel(ev);
          const detail = ev.data?.field
            ? ` · ${ev.data.field}`
            : ev.data?.primary_keyword ? ` · "${ev.data.primary_keyword}"` : "";
          return (
            <motion.li
              key={`${ev.ts}-${i}`}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: "1px dashed rgba(255,255,255,0.04)" }}
              data-testid={`cmd-live-event-${i}`}
            >
              <Icon size={12} style={{ color: "#a78bfa", flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <strong>{who}</strong> {what}{detail}
              </span>
              <small className="studio-muted" style={{ fontVariantNumeric: "tabular-nums" }}>{when}</small>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

